import styled from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useLazyResolveDataTokens } from 'automation-ui-canvas/queries/resolveDataTokensQuery';
import { useSimulateActionExecution } from 'automation-ui-canvas/queries/simulateActionExecutionQuery';
import { useEffect, useState } from 'react';
import LogEvent from 'automation-ui-log-events/records/LogEvent/LogEvent';
import ActionOutputsSection from 'automation-ui-canvas/components/panels/debugger-panel/ActionOutputsSection';
import UIAlert from 'UIComponents/alert/UIAlert';
import useSingleActionTest from 'automation-ui-canvas/components/panels/debugger-panel/useSingleActionTest';
import RerunTestAlert from 'automation-ui-canvas/components/panels/debugger-panel/RerunTestAlert';
import { useHistory, useLocation } from 'react-router-dom';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { ActionInputsSection } from 'automation-ui-canvas/components/panels/debugger-panel/ActionInputsSection';
import { atomicQueryUpdate } from 'automation-ui-routing/historyUtils';
import { OPEN_QUERY_PARAM } from 'automation-ui-canvas/components/flow-header/menu-bar/MenuBar';
import RunStatusAlert from 'automation-ui-canvas/components/panels/debugger-panel/RunStatusAlert';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledDiv = styled.div.withConfig({
  displayName: "DebuggerPanelStepBody__StyledDiv",
  componentId: "tqpmsy-0"
})(["width:100%;overflow:auto;border-top:", " solid ", ";padding:", ";"], ({
  theme
}) => theme.borderWidth['100'], ({
  theme
}) => theme.color['border-primary-subtle'], ({
  theme
}) => theme.space['0']);
export const DebuggerPanelStepBody = ({
  flow,
  actionId,
  objectId
}) => {
  var _action$getActionType;
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const trackEvent = useTrackEvent();
  const action = actionId ? flow.getActionById(Number(actionId)) : undefined;
  const actionTypeId = (_action$getActionType = action === null || action === void 0 ? void 0 : action.getActionTypeId()) !== null && _action$getActionType !== void 0 ? _action$getActionType : queryParams.get('actionTypeId');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const {
    stagedAction,
    hasUntestedChanges,
    onResetTest,
    isTestingEnabled
  } = useSingleActionTest({
    actionId: actionId,
    actionTypeId: actionTypeId
  });
  const [simulateActionExecution, {
    data: simulateActionExecutionQueryData,
    loading: simulateActionExecutionQueryLoading,
    error: simulateActionExecutionQueryError
  }] = useSimulateActionExecution();
  const simulateActionExecutionData = simulateActionExecutionQueryData === null || simulateActionExecutionQueryData === void 0 ? void 0 : simulateActionExecutionQueryData.simulateActionExecution;
  const [simulateActionExecutionDataCache, setSimulateActionExecutionDataCache] = useState(simulateActionExecutionData);
  useEffect(() => {
    if (!simulateActionExecutionQueryLoading && simulateActionExecutionData) {
      setSimulateActionExecutionDataCache(simulateActionExecutionData);
    }
  }, [simulateActionExecutionData, simulateActionExecutionQueryLoading]);
  const testBodyData = {
    flowId: flow.getFlowId(),
    objectTypeId: flow.getObjectTypeId(),
    objectId: objectId,
    action: stagedAction || action
  };
  const [getResolvedDataTokens, {
    data: resolvedDataTokensData,
    loading: resolvedDataTokensDataLoading
  }] = useLazyResolveDataTokens(testBodyData);
  const resolvedDataTokens = resolvedDataTokensData === null || resolvedDataTokensData === void 0 ? void 0 : resolvedDataTokensData.resolveDataTokens;

  // Refetch every time actionId or objectId changes
  useEffect(() => {
    if (actionId && (action || stagedAction)) {
      trackEvent(Trackables.INTERACT_WITH_SINGLE_ACTION_TESTING, {
        subAction: 'Single action test panel loaded',
        value: actionTypeId
      });
      setIsInitialLoad(true);
      simulateActionExecution({
        variables: testBodyData,
        onCompleted() {
          setIsInitialLoad(false);
        }
      });
      getResolvedDataTokens({
        variables: testBodyData
      });
      onResetTest();
    } else {
      // There was no initial load since there was no actionId
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId, objectId]);
  useEffect(() => {
    if (!actionId || !isTestingEnabled) {
      atomicQueryUpdate({}, [OPEN_QUERY_PARAM], history);
    }
  }, [actionId, isTestingEnabled, history]);
  if (isInitialLoad && simulateActionExecutionQueryLoading) {
    return /*#__PURE__*/_jsx(StyledDiv, {
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        layout: "centered",
        margin: "400",
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "debuggerPanel.step.loading"
        }),
        showLabel: true
      })
    });
  }
  if (simulateActionExecutionQueryError) {
    return /*#__PURE__*/_jsxs("div", {
      className: "m-all-5",
      children: [/*#__PURE__*/_jsx(UIAlert, {
        className: "m-bottom-3",
        type: "danger",
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "debuggerPanel.step.execution.runStatus.generalError"
        })
      }), /*#__PURE__*/_jsx(RerunTestAlert, {
        loading: simulateActionExecutionQueryLoading,
        onResetTest: onResetTest,
        onClick: () => {
          void simulateActionExecution({
            variables: testBodyData
          });
          getResolvedDataTokens({
            variables: testBodyData
          });
        }
      })]
    });
  }
  const emptyState = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(EmptyState, {
      marginBlockEnd: "300",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "debuggerPanel.step.testYourAction"
      }),
      primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "debuggerPanel.step.noActionToTest"
      })
    }), /*#__PURE__*/_jsx(Flex, {
      justifyContent: "space-around",
      children: /*#__PURE__*/_jsx(Illustration, {
        name: "automated-testing",
        width: 150
      })
    })]
  });
  return /*#__PURE__*/_jsx(StyledDiv, {
    children: /*#__PURE__*/_jsxs("div", {
      className: "p-x-10",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "p-top-4",
        children: [hasUntestedChanges && /*#__PURE__*/_jsx(RerunTestAlert, {
          loading: simulateActionExecutionQueryLoading,
          onResetTest: onResetTest,
          onClick: () => {
            void simulateActionExecution({
              variables: testBodyData
            });
            getResolvedDataTokens({
              variables: testBodyData
            });
          }
        }), simulateActionExecutionDataCache && objectId && /*#__PURE__*/_jsx(RunStatusAlert, {
          event: new LogEvent(simulateActionExecutionDataCache.events[0]),
          objectId: objectId,
          flow: flow
        })]
      }), (!action || !stagedAction) && !simulateActionExecutionDataCache ? emptyState : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(View, {
          marginBlockStart: "400",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h6",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "debuggerPanel.step.outputs.label"
            })
          }), /*#__PURE__*/_jsx(Text, {
            as: "p",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "debuggerPanel.step.outputs.helpText"
            })
          }), /*#__PURE__*/_jsx(ActionOutputsSection, {
            logEvent: simulateActionExecutionDataCache === null || simulateActionExecutionDataCache === void 0 ? void 0 : simulateActionExecutionDataCache.events[0],
            actionTypeId: actionTypeId,
            actionId: actionId
          })]
        }), !resolvedDataTokensDataLoading && resolvedDataTokens && /*#__PURE__*/_jsxs(View, {
          marginBlockStart: "400",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h6",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "debuggerPanel.step.currentSources.label"
            })
          }), /*#__PURE__*/_jsx(Text, {
            as: "p",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "debuggerPanel.step.currentSources.helpText"
            })
          }), /*#__PURE__*/_jsx(ActionInputsSection, {
            resolvedDataTokensData: resolvedDataTokens
          })]
        })]
      })]
    })
  });
};
export default DebuggerPanelStepBody;